import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileSearch, ShieldCheck, Scale, Rocket, Unlock, Users, BarChart3, Plus, Minus, Quote, ArrowRight, ClipboardList } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import InvestorPop from '../../utils/popMenu/InvestorPop';
import { StackSection, Reveal, SectionHeading } from '../../components/scroll';
import { GovernanceGapChart, TiltCard, ScrambleText, BlobGlassCard, TrustLoop, EqualizerBars, QualityComparator, InvestSignals } from '../../components/infographics';
import { StarsBackground } from '../../components/StarsBackground';
import { imgGoldCubes } from '../../components/sticky-backgrounds';
import { PageRoutes } from '../../utils/pageRoutes';
import { Context } from '../../contextApi/contextProvider';

const reasons = [
  { icon: Rocket, title: 'Driving digital transformation', body: 'As organisations strive to modernise, TICCA provides foundational building blocks for accountable, AI-assisted work rather than another disconnected tool.' },
  { icon: Unlock, title: 'Breaking conventional barriers', body: 'Traditional work environments set fixed processes that are slow to change. TICCA is built to bring intent, evidence, and governance into the flow of work.' },
  { icon: Users, title: 'Accessibility from day one', body: 'TICCA is designed to be useful immediately, so teams of every size can adopt accountable AI work without a long implementation project.' },
  { icon: BarChart3, title: 'Data-driven decision making', body: 'Ethical AI, analytics, and evidence let organisations harness their own operational data for clearer insight and better decisions.' },
];

const mantra = [
  'Do the right thing.',
  'Do things the right way, first time.',
  'Do not take shortcuts.',
  'Always go the extra effort.',
  'Be both accurate and precise.',
  'If it is not broken, do not fix it.',
  'Be thorough in all we do.',
  'Hold the standard. Every time.',
];

const essentialValues = [
  { title: 'Loyalty', body: "At TICCA, loyalty embodies an unwavering commitment to our company's mission and objectives. Our Team Members consistently place TICCA's success as our top priority, demonstrating steadfast dedication and a profound sense of belonging. We are deeply invested in the company's long-term growth and support one another through various challenges." },
  { title: 'Integrity', body: "Integrity is the cornerstone of our work ethic at TICCA. Team Members are transparent and honest in all of our dealings, ensuring that our actions align with the company's values. We maintain high ethical standards, fostering trust and respect within the team and with our clients." },
  { title: 'Ethics', body: 'Ethical behavior is non-negotiable at TICCA. Our team members adhere to stringent moral principles, making decisions that reflect our unwavering commitment to doing what is right, true and fair. We uphold ethical practices in all aspects of our work, from client interactions to internal processes, ensuring fairness and accountability.' },
  { title: 'Honesty', body: 'Honesty is integral to the culture at TICCA. Team members communicate openly and truthfully, creating an environment of trust and collaboration. They are straightforward in their feedback and transparent about challenges, promoting a culture of constructive problem-solving.' },
  { title: 'Dedication', body: "Dedication is evident in the exceptional commitment our Team Members show to our roles at TICCA. We consistently go above and beyond to achieve company objectives, ensuring high-quality results and driving the company's growth and innovation through our persistent efforts." },
  { title: 'Punctuality', body: "Punctuality reflects our respect for time and reliability at TICCA. Team members consistently meet deadlines and arrive on time for meetings, demonstrating their professionalism and consideration for others' schedules. This reliability ensures smooth operations and fosters a productive work environment." },
];

const influencingValues = [
  { title: 'Adaptability', body: "TICCA's members excel in adaptability, swiftly adjusting to new challenges and environments. Their flexibility ensures that the organization remains resilient and responsive to change. We quickly pivot strategies when needed, ensuring the company stays ahead in a dynamic market." },
  { title: 'Pro-activeness', body: 'Pro-activeness is a key trait of TICCA team members, who anticipate challenges and take initiative to address them before they escalate. We are forward-thinking and constantly seeking opportunities for improvement and innovation. This proactive approach ensures continuous growth and efficiency.' },
  { title: 'Persistence', body: 'Persistence is a defining trait at TICCA, where team members exhibit unwavering determination to overcome obstacles. Our resilience and focus ensure that projects are successfully completed and goals are achieved, even in the face of setbacks. Our perseverance ensures that projects are completed successfully and objectives are met.' },
  { title: 'Dependability', body: 'Dependability is crucial to the success of TICCA. Our team members consistently fulfill their commitments, ensuring reliability and trust within the team and with clients. This steadfastness guarantees smooth project execution and timely completion.' },
  { title: 'Humbleness', body: "Humbleness at TICCA is reflected in the willingness to listen, learn, and grow. Our team members recognize the value of collaboration and are open to feedback. This humility fosters a supportive and inclusive work environment where everyone's contributions are valued." },
  { title: 'Selflessness', body: "Selflessness is exemplified by TICCA team members who prioritize the team's success over individual recognition. We are willing to go the extra mile to support their colleagues and contribute to the company's goals. This altruistic spirit strengthens team cohesion and morale." },
  { title: 'Self-Confidence', body: "Self-confidence at TICCA means having the assurance to take risks and make decisions. Team members' belief in their expertise enables them to lead initiatives and drive innovation, inspiring trust and motivation among their peers." },
  { title: 'Self-Motivation', body: 'Self-motivation drives TICCA team members to consistently strive for excellence. We are driven by an internal desire to achieve their best, setting and exceeding personal and professional goals. This intrinsic motivation ensures high productivity and continual improvement.' },
  { title: 'Positiveness', body: 'Positiveness is a defining attribute at TICCA, where team members maintain a positive attitude even in challenging situations. Our optimism and enthusiasm foster a supportive and energetic work environment, encouraging everyone to stay motivated and focused.' },
  { title: 'Optimism', body: "Optimism at TICCA means looking forward to the future with hope and confidence. Team members believe in the company's mission and are enthusiastic about our potential. This positive outlook helps navigate challenges and seize opportunities with a constructive mindset." },
  { title: 'Eagerness', body: "Eagerness fuels TICCA's members' enthusiasm for our work. Our keen interest and willingness to learn drive the organization forward." },
  { title: 'Leadership', body: "Leadership is exemplified by TICCA's team members through our ability to inspire, guide, and support their colleagues. Our leadership fosters a culture of excellence and collaboration. We all set an example through our actions and foster a culture of excellence and collaboration." },
];

const problems = [
  { title: 'AI adoption is accelerating faster than management control', body: 'The need is an operating layer for accountable AI work.' },
  { title: 'AI slop and hallucinations can reach business workflows', body: 'Improve intent, structure, review, evidence, and governance before output is trusted.' },
  { title: 'Executives need accountability, not just automation', body: 'Make responsibility, approvals, records, and proof part of the workflow.' },
  { title: 'Teams need safe places to test AI work', body: 'Use controlled environments, sandboxes, permissions, and guardrails.' },
  { title: 'Enterprises need risk visibility', body: 'Capture governance signals, evidence, decisions, and accepted risk.' },
  { title: 'Boards need proof, not promises', body: 'Governance requires records that can be shown, audited, and stood behind.' },
];

const needs = [
  { icon: ClipboardList, label: 'Plan', body: 'Every AI action begins with a defined intent, scope, and expected outcome.' },
  { icon: FileSearch, label: 'Evidence', body: 'Every AI output carries proof of what changed and why.' },
  { icon: ShieldCheck, label: 'Security', body: 'Controlled environments, sandboxes, and guarded execution.' },
  { icon: Scale, label: 'Governance', body: 'Audit trails, approved scopes, and board-visible risk signals.' },
  { icon: AlertTriangle, label: 'HumAIn responsibility', body: 'A human stays accountable. AI can assist; it cannot own the outcome.' },
];

function ValueCard({
  title,
  body,
  index,
  open,
  onToggle,
}: {
  title: string;
  body: string;
  index: number;
  open: boolean;
  onToggle: () => void;
}) {
  const ACCENTS = ['#074EDC', '#4AAC00', '#FF8C00', '#ED0216'] as const;
  const accent = ACCENTS[index % ACCENTS.length];
  return (
    <Reveal delay={index * 0.02}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full rounded-2xl border backdrop-blur-2xl p-5 text-left transition-all duration-200 hover:scale-[1.01]"
        style={{
          background: open ? `rgba(2,8,28,0.92)` : 'rgba(2,8,28,0.75)',
          borderColor: open ? `${accent}66` : `${accent}30`,
          boxShadow: open ? `0 4px 24px ${accent}18` : undefined,
          borderLeftWidth: 3,
          borderLeftColor: accent,
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <span className="font-medium" style={{ color: open ? accent : 'rgba(255,255,255,0.9)' }}>{title}</span>
          <span className="inline-flex shrink-0 rounded-lg p-1.5" style={{ background: `${accent}22`, color: accent }}>
            {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </span>
        </div>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <p className="mt-3 text-white/75 text-sm leading-relaxed">{body}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </Reveal>
  );
}

function ValueGroup({
  label,
  description,
  values,
}: {
  label: string;
  description: string;
  values: { title: string; body: string }[];
}) {
  const [openSet, setOpenSet] = useState<Set<string>>(new Set());
  const allOpen = openSet.size === values.length;

  const toggle = (title: string) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  const toggleAll = () => setOpenSet(allOpen ? new Set() : new Set(values.map((v) => v.title)));

  return (
    <>
      <Reveal className="mt-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.14em] text-blue-300">{label}</p>
          <p className="mt-2 max-w-2xl text-white/65">{description}</p>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 backdrop-blur-md px-4 py-2 text-blue-300 transition-all duration-200 hover:border-white/25 hover:bg-white/15"
        >
          {allOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </Reveal>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {values.map((v, i) => (
          <ValueCard
            key={v.title}
            title={v.title}
            body={v.body}
            index={i}
            open={openSet.has(v.title)}
            onToggle={() => toggle(v.title)}
          />
        ))}
      </div>
    </>
  );
}


export function WhyPage() {
  const [investorPop, setInvestorPop] = useState(false);
  const navigate = useNavigate();
  const { userLoginData } = useContext(Context);

  useEffect(() => {
    // Prevent browser scroll restoration so page always starts at top
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    document.title = "TICCA | WHY Choose Us";

    if (userLoginData.status === 200) return;

    const dismissed = sessionStorage.getItem("investor_stage0_popup_dismissed");
    if (dismissed) return;

    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPercent > 0.5) {
        setInvestorPop(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [userLoginData.status]);

  return (
    <div className="relative bg-slate-950">
      {investorPop && <InvestorPop setInvestorPop={setInvestorPop} />}
      <section
        className="relative overflow-hidden flex min-h-[70vh] items-center px-6 pt-28 pb-16"
      >
{/* Hero — static gradient background */}
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-slate-950/60 to-transparent pointer-events-none" />
        <StarsBackground />
        <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-2">
          <div className="text-left">
            <Reveal>
              <span className="mb-4 inline-block rounded-full border border-[#074EDC]/60 bg-[#074EDC]/15 px-4 py-1 uppercase tracking-[0.20em] text-[#74aaff] backdrop-blur-sm text-xs font-semibold">
                Why This Matters
              </span>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="text-balance text-white text-4xl md:text-5xl font-extrabold leading-[1.08]">Why AI needs accountable operating infrastructure.</h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-6 max-w-2xl text-white/95 lg:mx-0">
                Enterprises are being pushed to adopt AI faster than their governance, audit, security,
                and management systems can adapt. TICCA is building the operating layer that helps
                organisations use AI with clearer intent, safer execution, evidence, guardrails,
                sandboxes, and accountability.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.22} className="flex items-center justify-center">
            <InvestSignals />
          </Reveal>
        </div>
      </section>

    

      {/* Security shield, trust, governance, accountability */}
      <section className="relative py-20 px-6 sm:px-10">
        <Reveal>
          <div className="mb-10 text-center">
            <span className="mb-3 inline-block uppercase tracking-[0.14em] text-xs text-sky-300">Five requirements</span>
            <h2 style={{ color: '#4AAC00' }}>What accountable AI work demands.</h2>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {needs.map(({ icon: Icon, label, body }, i) => (
            <Reveal key={label} delay={i * 0.08}>
              <TiltCard tilt={4}>
                <div className="flex h-full items-start gap-5 rounded-2xl border border-white/12 border-l-4 border-l-blue-400/75 bg-white/8 backdrop-blur-md p-7 hover:bg-white/12 hover:border-white/22 transition-all duration-200">
                  <motion.div
                    whileHover={{ rotate: -8, scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="mt-0.5 shrink-0 rounded-xl bg-blue-400/20 p-3 border border-blue-300/30"
                  >
                    <Icon className="h-6 w-6 text-blue-300" />
                  </motion.div>
                  <div>
                    <h3 className="mb-1.5 text-white">{label}</h3>
                    <p className="text-white/70">{body}</p>
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.24}>
          <blockquote className="mt-12 rounded-2xl border-l-4 border-l-blue-400/75 bg-white/8 backdrop-blur-md pl-6 pr-6 py-5 text-center">
            <p className="text-white/85">
              "Accountability is not a feature you add later. It is the foundation you build on from the start."
            </p>
            <footer className="mt-3 uppercase tracking-[0.12em] text-xs text-blue-300">
              TICCA governance thesis
            </footer>
          </blockquote>
        </Reveal>
      </section>

      <StackSection theme="navy" imageSrc={imgGoldCubes}>
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <SectionHeading
            eyebrow="The Problem"
            theme="navy"
            title={<ScrambleText text="The governance gap created by rapid AI adoption." />}
            intro="AI capability is climbing far faster than the governance around it. The widening gap between the two is the operating risk TICCA is built to close."
          />
          <Reveal className="rounded-2xl border border-white/18 bg-white/10 backdrop-blur-md p-5">
            <div className="mx-auto max-w-md">
              <GovernanceGapChart />
            </div>
          </Reveal>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {problems.map(({ title, body }, i) => (
            <Reveal key={title} delay={i * 0.06}>
              <TiltCard>
                <div className="h-full rounded-2xl border border-white/12 bg-white/8 backdrop-blur-md p-7 hover:bg-white/12 hover:border-white/22 transition-all duration-200">
                  <h3 className="mb-2 text-white">{title}</h3>
                  <p className="text-white/70">{body}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </StackSection>

      <StackSection theme="navy">
        <SectionHeading
          eyebrow="What businesses need"
          theme="navy"
          title="Capability with control."
          intro="Businesses need AI productivity, but they also need evidence, security, cost control, governance, and HumAIn responsibility. TICCA is focused on this gap."
        />
        <Reveal className="mt-10">
          <QualityComparator />
        </Reveal>
      </StackSection>

      {/* WHY TICCA MATTERS */}
      <StackSection theme="glass">
        <SectionHeading
          eyebrow="Why TICCA"
          theme="glass"
          title="The reasons TICCA matters now."
          intro="Beyond the governance gap, several forces make an accountable operating layer valuable to organisations adopting AI at speed."
        />
        <div className="mt-10 grid items-center gap-10 lg:grid-cols-[320px_1fr]">
          <Reveal className="flex justify-center">
            <TrustLoop />
          </Reveal>
          <Reveal delay={0.1} className="rounded-2xl border border-white/12 bg-white/8 backdrop-blur-md p-7">
            <p className="mb-5 text-white/70">
              Intent, evidence, and accountability form a continuous loop that keeps AI-assisted
              work trustworthy at operating scale.
            </p>
            <div className="flex h-10 items-end">
              <EqualizerBars bars={7} />
            </div>
          </Reveal>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {reasons.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 0.06}>
              <TiltCard>
                <BlobGlassCard theme="navy" blobs={2}>
                  <div className="flex h-full items-start gap-5 p-7">
                    <motion.div
                      whileHover={{ rotate: -8, scale: 1.08 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                      className="inline-flex flex-shrink-0 rounded-xl bg-blue-400/20 p-3 border border-blue-300/30"
                    >
                      <Icon className="h-6 w-6 text-blue-300" />
                    </motion.div>
                    <div>
                      <h3 className="mb-2 text-white">{title}</h3>
                      <p className="text-white/70">{body}</p>
                    </div>
                  </div>
                </BlobGlassCard>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </StackSection>

      {/* MANTRA / WORK ETHOS */}
      <StackSection theme="navy">
        <SectionHeading
          eyebrow="Work Ethos"
          theme="navy"
          align="center"
          title="The mantras that govern how TICCA works."
          intro="Execution discipline is a control, not a slogan. These operating rules keep AI-assisted work accurate, accountable, and trustworthy at every step."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {mantra.map((line, i) => {
            const accent = (['#074EDC','#4AAC00','#FF8C00','#ED0216'] as const)[i % 4];
            return (
              <Reveal key={line} delay={i * 0.04}>
                <div
                  className="flex h-full items-start gap-3 rounded-2xl border p-5 backdrop-blur-md transition-all duration-200 hover:scale-[1.02]"
                  style={{ background: `rgba(2,8,28,0.88)`, borderColor: `${accent}44`, boxShadow: `0 2px 16px ${accent}12` }}
                >
                  <Quote className="h-5 w-5 shrink-0 mt-0.5" style={{ color: accent }} />
                  <p className="text-white/85 text-sm leading-relaxed">{line}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </StackSection>

      {/* FOUNDATION / VALUES */}
      <StackSection theme="navy">
        <SectionHeading
          eyebrow="Our Foundation"
          theme="navy"
          align="center"
          title="The values that drive our excellence."
          intro="The character we hire for and hold ourselves to. Click any value to discover what drives our excellence."
        />

        <ValueGroup
          label="Essential character"
          description="The attributes that are non-negotiable to become and remain part of the TICCA team."
          values={essentialValues}
        />
        <ValueGroup
          label="Influencing characteristics"
          description="The desirable qualities that shape merit, growth, and the way we lead one another."
          values={influencingValues}
        />
      </StackSection>

      {/* ── DESIGN PARTNER PROGRAMME ──────────────────────────── */}
      <StackSection theme="glass">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div
              className="rounded-2xl border-2 bg-[rgba(2,8,28,0.88)] backdrop-blur-2xl p-10 text-center"
              style={{
                borderColor: 'rgba(74,172,0,0.30)',
                boxShadow: '0 0 60px -12px rgba(74,172,0,0.15)',
              }}
            >
              <span
                className="inline-block rounded-full border px-4 py-1 text-xs font-bold uppercase tracking-[0.20em]"
                style={{
                  background: 'rgba(74,172,0,0.15)',
                  borderColor: 'rgba(74,172,0,0.40)',
                  color: '#4AAC00',
                }}
              >
                Early Adopter Programme
              </span>
              <h2 className="mt-6 font-display text-3xl font-bold text-white md:text-4xl">
                TICCA is selecting design partners.
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-white/75 leading-relaxed">
                TICCA is inviting organisations with meaningful AI governance needs to participate
                in its early adopter programme. Design partners will work directly with the founding
                team to shape the operating layer for real-world AI workflows. Interested
                organisations are invited to apply through the investor route.
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(PageRoutes.InvestorCenter)}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#074EDC] to-[#0540b8] px-8 py-4 text-white shadow-[0_0_40px_-8px_rgba(7,78,220,0.5)]"
              >
                Request investor access
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </motion.button>
              <motion.a
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                href="/contact"
                className="mt-8 ml-4 inline-flex items-center gap-2 rounded-full border border-white/45 bg-white/8 px-8 py-4 text-white backdrop-blur-sm hover:bg-white/14 transition-colors"
              >
                Contact us
              </motion.a>
            </div>
          </Reveal>
        </div>
      </StackSection>
    </div>
  );
}


export default WhyPage;
